/**
 * Utility for mapping partner codes to display data
 * Used for both passthrough and Kayak meta redirect flows
 *
 * This is the single source of truth for all partner mappings.
 */

import { logWarning } from '@/app/utils/logger'

export interface PartnerDisplayData {
	name: string
	displayName: string
}

/** Default fallback partner data when no mapping is found */
export const DEFAULT_PARTNER: PartnerDisplayData = {
	name: 'partner',
	displayName: 'our partner',
} as const

/** Kayak partner data - used as default for km (Kayak meta) flows */
export const KAYAK_PARTNER: PartnerDisplayData = {
	name: 'kayak',
	displayName: 'Kayak.com',
} as const

/**
 * Primary partner map - maps partner codes (f param) to display data
 * Includes both short codes and fuid values
 */
const partnersMap: Record<string, PartnerDisplayData> = {
	// Partner codes (f param values)
	b: { name: 'booking', displayName: 'Booking.com' },
	bb: { name: 'booking', displayName: 'Booking.com' },
	k: { name: 'kayak', displayName: 'Kayak.com' },
	s: { name: 'skyscanner', displayName: 'Skyscanner' },
	ex: { name: 'expedia', displayName: 'Expedia' },
	cz: { name: 'tiki', displayName: 'Tiki' },
	vio: { name: 'vio', displayName: 'Vio.com' },
	aga: { name: 'agoda', displayName: 'Agoda.com' },
	h: { name: 'hotels', displayName: 'Hotels.com' },
	// Short codes (fuid values)
	bok: { name: 'booking', displayName: 'Booking.com' },
	exp: { name: 'expedia', displayName: 'Expedia' },
	hot: { name: 'hotels', displayName: 'Hotels.com' },
}

/**
 * Secondary partner map - maps numeric fuid values to display data
 */
const partnersSecondaryMap: Record<string, PartnerDisplayData> = {
	'9000': { name: 'vio', displayName: 'Vio.com' },
	'9001': { name: 'expedia', displayName: 'Expedia' },
	'9002': { name: 'hotels', displayName: 'Hotels.com' },
	'9003': { name: 'agoda', displayName: 'Agoda.com' },
	'9004': { name: 'trip', displayName: 'Trip.com' },
	'9005': { name: 'booking', displayName: 'Booking.com' },
}

/**
 * Maps fuid to front-tab partner code (f param) for fallback redirects
 * Used when Kayak meta flow fails and we need to redirect to regular passthru
 */
export const FUID_TO_FRONT_PARTNER_MAP: Record<string, string> = {
	'bok': 'b',      // Booking.com
	'aga': 'k',      // Agoda -> fallback to Kayak
	'exp': 'ex',     // Expedia
	'hot': 'k',      // Hotels.com -> fallback to Kayak
	'9000': 'vio',   // Vio.com
	'9001': 'ex',    // Expedia
	'9002': 'k',     // Hotels.com -> fallback to Kayak
	'9003': 'k',     // Agoda -> fallback to Kayak
	'9004': 'k',     // Trip.com -> fallback to Kayak
	'9005': 'k',     // Booking.com -> fallback to Kayak
} as const

/** Default front-tab partner for fallback redirects */
export const DEFAULT_FRONT_PARTNER = 'k'

/**
 * Gets partner display data from partner code (f param) or uid (fuid param)
 * Checks uid first (fuid takes precedence), then partner code
 * @param partnerCode - The 'f' parameter from URL (e.g., 'k', 'b', 'km')
 * @param uid - The 'fuid' parameter from URL (e.g., '9005', 'bok')
 * @returns Partner display data with name and displayName
 */
export const getPartnerDisplayData = (
	partnerCode?: string | null,
	uid?: string | null
): PartnerDisplayData => {
	// Check uid first (fuid takes precedence)
	if (uid) {
		const fromSecondary = partnersSecondaryMap[uid]
		if (fromSecondary) return fromSecondary

		const fromPrimary = partnersMap[uid]
		if (fromPrimary) return fromPrimary
	}

	// Then check partner code
	if (partnerCode) {
		const fromPrimary = partnersMap[partnerCode]
		if (fromPrimary) return fromPrimary

		const fromSecondary = partnersSecondaryMap[partnerCode]
		if (fromSecondary) return fromSecondary
	}

	return DEFAULT_PARTNER
}

/**
 * Gets front-tab partner code for fallback redirects
 * @param fuid - The fuid parameter from URL
 * @returns Front-tab partner code (e.g., 'b', 'ex')
 */
export const getFrontPartnerFromFuid = (fuid: string | null | undefined): string => {
	if (!fuid) return DEFAULT_FRONT_PARTNER
	return FUID_TO_FRONT_PARTNER_MAP[fuid] || DEFAULT_FRONT_PARTNER
}

/**
 * Checks if partner data is the default fallback
 * @param partnerData - Partner display data to check
 * @returns true if this is the default fallback partner
 */
export const isDefaultPartner = (partnerData: PartnerDisplayData): boolean => {
	return partnerData.displayName === DEFAULT_PARTNER.displayName
}

/**
 * Gets partner display data with Kayak override for km (Kayak meta) flows.
 * When f=km and no specific partner is found via fuid, defaults to Kayak.
 * @param partnerCode - The 'f' parameter from URL (e.g., 'k', 'b', 'km')
 * @param uid - The 'fuid' parameter from URL (e.g., '9005', 'bok')
 * @returns Partner display data with name and displayName
 */
export const getPartnerDisplayDataWithKayakFallback = (
	partnerCode?: string | null,
	uid?: string | null
): PartnerDisplayData => {
	const partnerData = getPartnerDisplayData(partnerCode, uid)

	// When f=km (Kayak meta flow), show "Kayak" if no specific partner found
	if (partnerCode === 'km' && isDefaultPartner(partnerData)) {
		return KAYAK_PARTNER
	}

	return partnerData
}

const PARTNER_NAME_TO_CODE: Record<string, string> = {
	booking: 'b',
	kayak: 'k',
	expedia: 'ex',
	vio: 'vio',
	agoda: 'aga',
	hotels: 'h',
}

export type NormalizedPartnerParams = {
	f?: string
	fuid?: string
	b?: string
	buid?: string
}

export const normalizeFrontPartnerParams = (
	f?: string | null,
	fuid?: string | null
): { f?: string; fuid?: string } => {
	const safeF = f?.toLowerCase()
	const safeFuid = fuid?.toLowerCase()
	let normalizedF = safeF

	if (safeFuid && normalizedF && normalizedF !== 'km') {
		const fromUid = getPartnerDisplayData(undefined, safeFuid)
		const fromCode = normalizedF ? getPartnerDisplayData(normalizedF, undefined) : undefined
		if (
			fromCode &&
			!isDefaultPartner(fromUid) &&
			!isDefaultPartner(fromCode) &&
			fromUid.name !== fromCode.name
		) {
			// Priority to F parameter (unless km)
			// We clear fuid so that display logic uses F
			logWarning('Contradictory front partner params: f and fuid do not match (prioritizing f, clearing fuid)', {
				f: safeF,
				fuid: safeFuid,
				normalizedF,
			})
			return { f: normalizedF, fuid: undefined }
		}
	}

	return { f: normalizedF, fuid: safeFuid }
}

export const normalizeBackPartnerParams = (
	b?: string | null,
	buid?: string | null
): { b?: string; buid?: string } => {
	const safeB = b?.toLowerCase()
	const safeBuid = buid?.toLowerCase()
	let normalizedB = safeB

	if (safeBuid && normalizedB) {
		const fromUid = getPartnerDisplayData(undefined, safeBuid)
		const fromCode = normalizedB ? getPartnerDisplayData(normalizedB, undefined) : undefined
		if (
			fromCode &&
			!isDefaultPartner(fromUid) &&
			!isDefaultPartner(fromCode) &&
			fromUid.name !== fromCode.name
		) {
			// B parameter determines site destination
			// buid is kept as tracking ID (doesn't affect site selection)
			return { b: normalizedB, buid: safeBuid }
		}
	}

	return { b: normalizedB, buid: safeBuid }
}

export const normalizePartnerParams = (
	params: NormalizedPartnerParams
): NormalizedPartnerParams => {
	const { f, fuid } = normalizeFrontPartnerParams(params.f, params.fuid)
	const { b, buid } = normalizeBackPartnerParams(params.b, params.buid)

	return {
		...params,
		f,
		fuid,
		b,
		buid,
	}
}
